import { Box, CloseButton, Group, ScrollArea, Stack, Text } from '@mantine/core'

import { DEFAULT_BOGGLE_PLAYGROUND_SETTINGS } from '~/boggle/config/playground'
import { normalizeBogglePlaygroundSettings } from '~/boggle/lib/playground'
import {
  BOGGLE_RUN_SUMMARY_TIER_CATEGORIES,
  compareEarnedBadgesForSummaryDisplay,
  getTierForMetric,
  type BoggleRunSummaryTierLine,
} from '~/boggle/config/runSummaryScore'
import { getBoggleBadgeDefinition } from '~/boggle/config/badges/registry'
import type { BoggleBadgeDefinition } from '~/boggle/config/badges/types'
import type { BoggleCompletedRun } from '~/boggle/progress/types'
import { BackButton, IconButton } from '~/boggle/ui/atoms/Buttons'
import { RampingNumberText } from '~/boggle/ui/atoms/RampingNumberText'
import { BadgeMedallion } from '~/boggle/ui/compendium/BadgeMedallion'
import { BadgePopover } from '~/boggle/ui/compendium/BadgePopover'
import { RatingBadge } from '~/boggle/ui/ratings/RatingBadge'
import { GoldTilesRunTracker } from '~/boggle/ui/run/GoldTilesRunTracker'
import { MeterGauge } from '~/boggle/ui/run/MeterGauge'
import { RunSummaryTierLine } from '~/boggle/ui/run/RunSummary'
import { RunDetailsLettersPlayed } from '~/boggle/ui/history/RunDetailsLettersPlayed'
import {
  buildRunDetailsWordItems,
  getRunAcceptedWords,
  getRunDetailsWordsPlayedSummaryParts,
} from '~/boggle/ui/history/runDetailsWordItems'
import { BoggleWordList } from '~/boggle/ui/words/Words'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'

import { ArrowLeftIcon, ArrowRightIcon } from '@phosphor-icons/react'
import { BogglePlayedDateTime } from '~/boggle/ui/atoms/BogglePlayedDateTime'

const RUN_DETAILS_SCORE_RAMP_MS = 250
const RUN_DETAILS_SCORE_RAMP_REFERENCE_DELTA = 20
const RUN_DETAILS_SCORE_RAMP_MIN_MS = 120

export function RunDetails({
  hasNext,
  hasPrevious,
  onBack,
  onClose,
  onNavigateNext,
  onNavigatePrevious,
  run,
}: {
  hasNext: boolean
  hasPrevious: boolean
  onBack: () => void
  onClose?: () => void
  onNavigateNext: () => void
  onNavigatePrevious: () => void
  run: BoggleCompletedRun
}) {
  const tierLines = buildRunDetailsTierLines(run)
  const longestWord = getRunLongestWord(run)
  const goldTilesPlayed = run.summarySnapshot?.goldTilesPlayed ?? 0
  const meterSettings = normalizeBogglePlaygroundSettings(
    run.setupSnapshot?.resolvedPlaygroundSettings,
    DEFAULT_BOGGLE_PLAYGROUND_SETTINGS,
  ).meter
  const meterValue = run.summarySnapshot?.totalMeterEarned ?? 0
  const overmeterValue = run.summarySnapshot?.totalOvermeterEarned ?? 0
  const wordItems = buildRunDetailsWordItems(run)
  const wordsPlayedSummary = getRunDetailsWordsPlayedSummaryParts(wordItems)
  const earnedBadgeDefinitions = [...(run.earnedBadges ?? [])]
    .sort(compareEarnedBadgesForSummaryDisplay)
    .map(earned => ({
      definition: getBoggleBadgeDefinition(earned.badgeId),
      earnedAtLedgerIndex: earned.earnedAtLedgerIndex,
    }))
    .filter(
      (
        badge,
      ): badge is { definition: BoggleBadgeDefinition; earnedAtLedgerIndex: number } =>
        Boolean(badge.definition),
    )

  return (
    <Stack
      data-testid={`boggle-run-details-${run.id}`}
      gap={12}
      h="100%"
      mih={0}
      pos="relative"
    >
      <Group align="flex-start" gap={8} justify="space-between" px={10} wrap="nowrap">
        <BackButton
          bdrs={50}
          data-testid="run-details-back"
          fz={18}
          iconSize={16}
          h={34}
          onClick={onBack}
          w={100}
        >
          Back
        </BackButton>
        <Box
          data-testid="run-details-title"
          flex={1}
          style={{ pointerEvents: 'none' }}
          pt={4}
        >
          <BogglePlayedDateTime
            align="center"
            isoString={run.completedAt}
            testIdPrefix="run-details-played"
          />
        </Box>
        <Group gap={4} wrap="nowrap" justify="flex-end" miw={100}>
          {hasPrevious ? (
            <IconButton
              aria-label="Previous run"
              color="dark.7"
              c="white"
              bdrs={4}
              bd="1px solid rgba(255, 255, 255, 0.2)"
              data-testid="run-details-nav-previous"
              onClick={onNavigatePrevious}
              size={44}
            >
              <IconGlyph
                icon={ArrowLeftIcon}
                size={24}
                weight="bold"
                style={{ filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.5))' }}
              />
            </IconButton>
          ) : null}
          {hasNext ? (
            <IconButton
              aria-label="Next run"
              color="dark.7"
              c="white"
              bdrs={4}
              bd="1px solid rgba(255, 255, 255, 0.2)"
              data-testid="run-details-nav-next"
              onClick={onNavigateNext}
              size={44}
            >
              <IconGlyph
                icon={ArrowRightIcon}
                size={24}
                weight="bold"
                style={{ filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.5))' }}
              />
            </IconButton>
          ) : null}
          {onClose ? (
            <CloseButton
              aria-label="Close run details"
              c="white"
              data-testid="run-details-close"
              iconSize={28}
              onClick={onClose}
              variant="transparent"
            />
          ) : null}
        </Group>
      </Group>

      <ScrollArea flex={1} mih={0} type="hover" px={14} scrollbars="y">
        <Stack gap={20} pt={20} pb={20}>
          <Group
            align="stretch"
            data-testid="run-details-row-core"
            gap={10}
            grow
            wrap="nowrap"
          >
            <RunDetailsRating run={run} />
            <RunDetailsScore score={run.summarySnapshot?.total} />
            <Stack
              align="center"
              data-testid="run-details-gold-meter"
              gap={0}
              justify="center"
              pos="relative"
            >
              <MeterGauge
                diameter={80}
                fillGainPulseEnabled={false}
                fontMetricsDiameter={82}
                marginTop={0}
                overmeterValue={overmeterValue}
                progressMeterWidth={8}
                settings={meterSettings}
                value={meterValue}
              />
              <Box
                pos="absolute"
                top={44}
                left={6}
                right={0}
                bottom={0}
                style={{ transform: 'scale(0.8)' }}
              >
                <GoldTilesRunTracker
                  forceHidden={goldTilesPlayed <= 0}
                  inPlayPulseEnabled={false}
                  inPlaySpringEnabled={false}
                  goldTilesUsed={goldTilesPlayed}
                />
              </Box>
            </Stack>
          </Group>

          <Stack
            data-testid="run-details-row-tiers"
            align="center"
            gap={2}
            px={4}
            mb={30}
            style={{ transform: 'scale(0.8)' }}
          >
            {tierLines.map(line => (
              <Box
                data-testid={`run-details-tier-${line.category.id}`}
                key={line.category.id}
                w="100%"
              >
                <RunSummaryTierLine
                  isRevealed
                  line={line}
                  longestWord={longestWord}
                  metricRampDurationMs={200}
                />
              </Box>
            ))}
          </Stack>

          <Stack data-testid="run-details-row-badges" gap={6} align="center" mb={12}>
            {earnedBadgeDefinitions.length > 0 ? (
              <Group gap={0} wrap="wrap" justify="center">
                {earnedBadgeDefinitions.map(({ definition, earnedAtLedgerIndex }) => (
                  <BadgePopover
                    countsByRating={{}}
                    definition={definition}
                    frameTriggerWhenOpen
                    key={`${definition.id}:${earnedAtLedgerIndex}`}
                    p={4}
                  >
                    <BadgeMedallion
                      definition={definition}
                      highestEarnedRating={null}
                      locked={false}
                      size="sm"
                    />
                  </BadgePopover>
                ))}
              </Group>
            ) : null}
          </Stack>

          <RunDetailsLettersPlayed run={run} />

          <Stack data-testid="run-details-row-words" gap={12}>
            {wordItems.length > 0 ? (
              <Text
                c="rgba(232, 224, 210, 0.8)"
                data-testid="run-details-words-played-summary"
                fz={16}
                lh={1}
                ta="center"
              >
                {wordsPlayedSummary.primary}
                {wordsPlayedSummary.repeatsSuffix ? (
                  <Text c="rgb(255, 215, 129, 0.8)" component="span" fz={16}>
                    {' '}
                    {wordsPlayedSummary.repeatsSuffix}
                  </Text>
                ) : null}
              </Text>
            ) : null}
            <BoggleWordList
              emptyFallback={
                <Text c="rgba(232, 224, 210, 0.55)" fz={13} py={50}>
                  No words played
                </Text>
              }
              items={wordItems}
              testId="run-details-accepted-words-list"
            />
          </Stack>
        </Stack>
      </ScrollArea>

      <Text
        c="rgba(232, 224, 210, 0.55)"
        data-testid="run-details-seed"
        fz={12}
        lh={1}
        pos="absolute"
        bottom={2}
        right={8}
      >
        Seed {formatRunSeed(run)}
      </Text>
    </Stack>
  )
}

function RunDetailsScore({ score }: { score: number | undefined }) {
  const hasValidScore = typeof score === 'number' && Number.isFinite(score)

  return (
    <Stack align="center" data-testid="run-details-final-score" gap={6} p={10}>
      <Text c="rgba(232, 224, 210, 0.56)" fz={11} lh={1} tt="uppercase">
        Score
      </Text>
      {hasValidScore ? (
        <RampingNumberText
          behavior="standard"
          duration={RUN_DETAILS_SCORE_RAMP_MS}
          durationScaleReferenceValue={RUN_DETAILS_SCORE_RAMP_REFERENCE_DELTA}
          fz={38}
          maxValueVisual={Math.max(1, score)}
          minScaledDuration={RUN_DETAILS_SCORE_RAMP_MIN_MS}
          style={{
            color: 'white',
            fontFamily: 'Tilt Neon, sans-serif',
            fontWeight: 600,
            lineHeight: 1,
          }}
          value={score}
        />
      ) : (
        <Text c="white" ff="Tilt Neon, sans-serif" fw={600} fz={38} lh={1}>
          —
        </Text>
      )}
    </Stack>
  )
}

function RunDetailsRating({ run }: { run: BoggleCompletedRun }) {
  return (
    <Stack data-testid="run-details-rating" align="center">
      <RatingBadge h={80} ratingId={run.ratingId} w={80} />
    </Stack>
  )
}

function buildRunDetailsTierLines(run: BoggleCompletedRun): BoggleRunSummaryTierLine[] {
  const outcomesByCategory = new Map(
    run.summarySnapshot?.tierOutcomes.map(outcome => [outcome.categoryId, outcome]) ?? [],
  )

  return BOGGLE_RUN_SUMMARY_TIER_CATEGORIES.map(category => {
    const outcome = outcomesByCategory.get(category.id)
    const metricValue = outcome?.metricValue ?? 0
    const tierFromSnapshot = outcome?.tierLevel
      ? (category.tiers.find(tier => tier.level === outcome.tierLevel) ?? null)
      : null
    const tier = tierFromSnapshot ?? getTierForMetric(category.tiers, metricValue)

    return {
      category,
      metricValue,
      points: tier?.points ?? 0,
      tier,
    }
  })
}

function getRunLongestWord(run: BoggleCompletedRun) {
  const words = getRunAcceptedWords(run)
  if (words.length === 0) {
    return null
  }

  return words.reduce(
    (longest, word) => (word.length > longest.length ? word : longest),
    words[0]!,
  )
}

function formatRunSeed(run: BoggleCompletedRun) {
  const seed = run.setupSnapshot?.seedInput ?? run.setupSnapshot?.seed
  return seed === undefined || seed === null || seed === '' ? '—' : String(seed)
}
